import { useCallback, useState } from 'react';
import {
  ExceptionLogicOperatorAll,
  ExceptionLogicOperatorAny,
  ExceptionResourceTypeOperatory,
  ExceptionResourceTypeProvider,
  ExceptionResourceTypeService,
  ExceptionScopeTypeLocation,
  ExceptionScopeTypeResource,
  ScheduleException,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/exception.pb';
import { RRule } from 'rrule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getTodaysDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { formatDateTime } from '@frontend/schedule-helpers';
import { formatDate, useAlert, useForm, UseFormReturnType, FormFieldActionTypes } from '@frontend/design-system';
import { AvailabilityRecurrenceTypes } from '../../../../components/availability/availability-form/types';
import { AvailabilitySettingsTrackingIds } from '../../../../trackingIds/availability-settings-tracking';
import { getRecurrenceTypeForTracking } from '../../../../trackingIds/availability-tracking-helpers';
import { DATE_DISPLAY_FORMAT } from '../../../../utils/date-helpers';

const { DATETIME_FORMATS } = SchedulerV3.Helpers;

type TimePickerInterval = 10 | 15 | 30 | 60;

export type RuleExceptionFormTarget = 'ALL_RESOURCES' | 'SPECIFIC_RESOURCES' | 'RESOURCE_COMBINATION';

type RuleExceptionRecurrenceType = AvailabilityRecurrenceTypes | 'SINGLE_OCCURRENCE';

export type RuleExceptionFormConfig = {
  name: { type: 'text'; value: string; required: boolean };
  isRecurring: { type: 'switch'; value: boolean };
  recurrenceType: { type: 'dropdown'; value: RuleExceptionRecurrenceType; required: boolean };
  startDate: { type: 'date'; value: string; required: boolean }; // MM/DD/YYYY
  endDate: { type: 'date'; value: string }; // MM/DD/YYYY
  timeRange: { type: 'timeRange'; value: [string, string]; required: boolean; interval: TimePickerInterval }; // HH:mm:ss
  isAllDay: { type: 'checkbox'; value: boolean };
  target: { type: 'radio'; value: RuleExceptionFormTarget; required: boolean };
  logicOperator: { type: 'dropdown'; value: string };
  // SPECIFIC_RESOURCES multi-select fields
  providers: { type: 'checklist'; value: string[] };
  operatories: { type: 'checklist'; value: string[] };
  services: { type: 'checklist'; value: string[] };
  // RESOURCE_COMBINATION single-select fields
  provider: { type: 'dropdown'; value: string };
  operatory: { type: 'dropdown'; value: string };
  service: { type: 'dropdown'; value: string };
};

export type GetRuleExceptionFormFieldProps = UseFormReturnType<
  RuleExceptionFormConfig,
  keyof RuleExceptionFormConfig
>['getFieldProps'];

export type RuleExceptionFormValues = UseFormReturnType<
  RuleExceptionFormConfig,
  keyof RuleExceptionFormConfig
>['values'];

type UseAvailabilityRuleFormParams = {
  selectedException: ScheduleException | null;
  locationId: string;
  isEditMode: boolean;
  closeModal: () => void;
};

type ExceptionTargetResourceType =
  | typeof ExceptionResourceTypeProvider
  | typeof ExceptionResourceTypeOperatory
  | typeof ExceptionResourceTypeService;

type ExceptionTargetResource = {
  type: ExceptionTargetResourceType;
  id: string;
};

const parseRRule = (rRuleStr: string | undefined): RRule | undefined => {
  if (rRuleStr) {
    try {
      return RRule.fromString(rRuleStr);
    } catch (e) {
      console.error('Failed to parse RRule:', e);
      return undefined;
    }
  }
  return undefined;
};

export const useAddEditRuleExceptionForm = ({
  selectedException,
  locationId,
  isEditMode,
  closeModal,
}: UseAvailabilityRuleFormParams) => {
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();
  const [rRule, setRRule] = useState<RRule | undefined>(() => parseRRule(selectedException?.recurrenceRule));

  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const timezone = locationTimezoneMap[locationId]?.timezone;
  const todayDate = getTodaysDate(DATE_DISPLAY_FORMAT);
  const initialTarget = getInitialTarget(selectedException);
  const initialStartTime = initialTimeFormatter(selectedException?.time?.startTime, timezone) || '';
  const initialEndTime = initialTimeFormatter(selectedException?.time?.endTime, timezone) || '';

  const { isComplete, values, getFieldProps, reset, seedValues } = useForm<
    RuleExceptionFormConfig,
    keyof RuleExceptionFormConfig
  >({
    fields: {
      name: {
        type: 'text',
        value: selectedException?.name || '',
        required: true,
      },
      isRecurring: {
        type: 'switch',
        value: !!(selectedException?.recurrenceType && selectedException.recurrenceType !== 'SINGLE_OCCURRENCE'),
      },
      recurrenceType: {
        type: 'dropdown',
        value: getInitialRecurrenceType(selectedException?.recurrenceType),
        required: true,
      },
      startDate: {
        type: 'date',
        value: isEditMode ? initialDateFormatter(selectedException?.nextOccurrenceStart, timezone) : todayDate,
        required: true,
      },
      endDate: {
        type: 'date',
        value: isEditMode ? initialDateFormatter(selectedException?.endDate, timezone) : '',
      },
      timeRange: {
        type: 'timeRange',
        value: [initialStartTime, initialEndTime],
        interval: 30,
        required: true,
      },
      isAllDay: {
        type: 'checkbox',
        value: selectedException?.time === null,
      },
      target: {
        type: 'radio',
        value: initialTarget,
        required: true,
      },
      logicOperator: {
        type: 'dropdown',
        value: selectedException?.target?.logicOperator || '',
      },
      providers: {
        type: 'checklist',
        value:
          selectedException?.target?.resources
            ?.filter((r) => r.type === ExceptionResourceTypeProvider)
            .map((r) => r.id ?? '')
            .filter(Boolean) || [],
      },
      operatories: {
        type: 'checklist',
        value:
          selectedException?.target?.resources
            ?.filter((r) => r.type === ExceptionResourceTypeOperatory)
            .map((r) => r.id ?? '')
            .filter(Boolean) || [],
      },
      services: {
        type: 'checklist',
        value:
          selectedException?.target?.resources
            ?.filter((r) => r.type === ExceptionResourceTypeService)
            .map((r) => r.id ?? '')
            .filter(Boolean) || [],
      },
      provider: {
        type: 'dropdown',
        value: selectedException?.target?.resources?.find((r) => r.type === ExceptionResourceTypeProvider)?.id || '',
      },
      operatory: {
        type: 'dropdown',
        value: selectedException?.target?.resources?.find((r) => r.type === ExceptionResourceTypeOperatory)?.id || '',
      },
      service: {
        type: 'dropdown',
        value: selectedException?.target?.resources?.find((r) => r.type === ExceptionResourceTypeService)?.id || '',
      },
    },
    fieldStateReducer: (state, action) => {
      if (action.type === FormFieldActionTypes.Update) {
        const { name, value } = action.payload;

        if (name === 'isRecurring') {
          return {
            ...state,
            recurrenceType: {
              ...state.recurrenceType,
              value: value ? 'WEEKLY' : 'SINGLE_OCCURRENCE',
            },
          };
        }
      }

      return null;
    },
  });

  const createExceptionMutation = SchedulerV3.Mutations.useCreateScheduleExceptionMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Exception created successfully'));
        closeModal();
      },
      onError: () => {
        alert.error(t('Failed to create exception'));
      },
    },
  });

  const updateExceptionMutation = SchedulerV3.Mutations.useUpdateScheduleExceptionMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Exception updated successfully'));
        closeModal();
      },
      onError: () => {
        alert.error(t('Failed to update exception'));
      },
    },
  });

  const handleTargetChange = (newTarget: RuleExceptionFormConfig['target']['value']) => {
    seedValues({ target: newTarget });
  };

  const handleSubmit = async () => {
    const startTime = values.isAllDay ? null : values.timeRange?.[0];
    const endTime = values.isAllDay ? null : values.timeRange?.[1];

    const recurrenceRule = rRule ? rRule.toString().replace(/^RRULE:/, '') : undefined;

    const isLocationScope = values.target === 'ALL_RESOURCES';
    const isCombination = values.target === 'RESOURCE_COMBINATION';

    const scopeType = isLocationScope ? ExceptionScopeTypeLocation : ExceptionScopeTypeResource;

    const logicOperator = isLocationScope
      ? undefined
      : isCombination
      ? ExceptionLogicOperatorAll
      : ExceptionLogicOperatorAny;

    const resources = isLocationScope
      ? undefined
      : isCombination
      ? [
          values.provider && { type: ExceptionResourceTypeProvider, id: values.provider },
          values.operatory && { type: ExceptionResourceTypeOperatory, id: values.operatory },
          values.service && { type: ExceptionResourceTypeService, id: values.service },
        ].filter((resource): resource is ExceptionTargetResource => Boolean(resource))
      : [
          ...(values.providers ?? []).map((id) => ({ type: ExceptionResourceTypeProvider, id })),
          ...(values.operatories ?? []).map((id) => ({ type: ExceptionResourceTypeOperatory, id })),
          ...(values.services ?? []).map((id) => ({ type: ExceptionResourceTypeService, id })),
        ];

    const commonPayload = {
      locationId,
      name: values.name || '',
      startDate: formatDate(values.startDate, DATETIME_FORMATS.ISO_DATE),
      ...(values.endDate && {
        endDate:
          values.recurrenceType === 'SPECIFIC_DATE'
            ? formatDate(values.startDate, DATETIME_FORMATS.ISO_DATE)
            : formatDate(values.endDate, DATETIME_FORMATS.ISO_DATE),
      }),
      ...(startTime && {
        startTime: formatDateTime(startTime, {
          inputFormat: DATETIME_FORMATS.ISO_TIME,
          outputFormat: DATETIME_FORMATS.HOURS_MINUTES,
        }),
      }),
      ...(endTime && {
        endTime: formatDateTime(endTime, {
          inputFormat: DATETIME_FORMATS.ISO_TIME,
          outputFormat: DATETIME_FORMATS.HOURS_MINUTES,
        }),
      }),
      recurrenceType: values.recurrenceType,
      ...(values.isRecurring && { recurrenceRule }),
      scopeType,
      logicOperator,
      resources,
      timezone,
    };

    const { targetProviderIds, targetOperatoryIds, targetServiceTypeIds } = getTargetResourceIds(resources);

    SchedulerV3.trackPendoEvent(AvailabilitySettingsTrackingIds.events.exceptionSaveClicked, {
      locationId,
      name: values.name || '',
      recurrenceType: getRecurrenceTypeForTracking(values.recurrenceType),
      exceptionTarget: isLocationScope ? 'all' : isCombination ? 'and' : 'or',
      targetProviderIds: targetProviderIds,
      targetOperatoryIds: targetOperatoryIds,
      targetServiceTypeIds: targetServiceTypeIds,
      saveType: isEditMode ? 'update' : 'new',
    });

    if (isEditMode && selectedException?.id) {
      await updateExceptionMutation.mutateAsync({ ...commonPayload, id: selectedException.id });
    } else {
      await createExceptionMutation.mutateAsync(commonPayload);
    }
  };

  const handleRRuleChange = useCallback((nextRRule: RRule | undefined) => {
    setRRule(nextRRule);
  }, []);

  return {
    getFieldProps,
    values,
    isComplete,
    reset,
    handleSubmit,
    handleTargetChange,
    handleRRuleChange,
    rRule,
    seedValues,
    isSubmitting: createExceptionMutation.isLoading || updateExceptionMutation.isLoading,
  };
};

const getInitialRecurrenceType = (recurrenceType?: string): RuleExceptionRecurrenceType => {
  if (!recurrenceType) return 'SINGLE_OCCURRENCE';
  return recurrenceType as RuleExceptionRecurrenceType;
};

const getTargetResourceIds = (resources?: ExceptionTargetResource[]) => {
  return (resources ?? []).reduce(
    (acc, resource) => {
      if (resource.type === ExceptionResourceTypeProvider) acc.targetProviderIds.push(resource.id);
      if (resource.type === ExceptionResourceTypeOperatory) acc.targetOperatoryIds.push(resource.id);
      if (resource.type === ExceptionResourceTypeService) acc.targetServiceTypeIds.push(resource.id);
      return acc;
    },
    {
      targetProviderIds: [] as string[],
      targetOperatoryIds: [] as string[],
      targetServiceTypeIds: [] as string[],
    }
  );
};

const getInitialTarget = (
  selectedException: ScheduleException | null
): 'ALL_RESOURCES' | 'SPECIFIC_RESOURCES' | 'RESOURCE_COMBINATION' => {
  if (!selectedException?.target || selectedException.target.scopeType === ExceptionScopeTypeLocation) {
    return 'ALL_RESOURCES';
  }

  return selectedException.target.logicOperator === ExceptionLogicOperatorAll
    ? 'RESOURCE_COMBINATION'
    : 'SPECIFIC_RESOURCES';
};

/**
 * The API returns date and time in ISO format. To display the existing values in the form for editing,
 * we need to convert them to local date and time in MM/DD/YYYY and HH:mm format respectively based on the timezone of the location.
 */
const initialDateFormatter = (dateString?: string, timezone?: string): string => {
  return formatDateTime(dateString, {
    inputFormat: DATETIME_FORMATS.ISO_DATE,
    outputFormat: DATE_DISPLAY_FORMAT,
    toTimezone: timezone,
  });
};
const initialTimeFormatter = (timeString?: string, timezone?: string): string => {
  const formattedFromHoursMinutes = formatDateTime(timeString, {
    inputFormat: DATETIME_FORMATS.HOURS_MINUTES,
    outputFormat: DATETIME_FORMATS.ISO_TIME,
    toTimezone: timezone,
  });

  if (formattedFromHoursMinutes) {
    return formattedFromHoursMinutes;
  }

  // Fallback for sources that may already provide seconds.
  return formatDateTime(timeString, {
    inputFormat: DATETIME_FORMATS.ISO_TIME,
    outputFormat: DATETIME_FORMATS.ISO_TIME,
    toTimezone: timezone,
  });
};
